const isClient = typeof window !== 'undefined';

function readCookie(key) {
  if (!isClient) return null;

  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${encodeURIComponent(key)}=`));

  return cookie ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : null;
}

function writeCookie(key, value, options = {}) {
  if (!isClient) return;

  const {
    days,
    expires,
    path = '/',
    sameSite = 'Lax',
    secure = typeof window !== 'undefined' && window.location.protocol === 'https:',
  } = options;

  const cookieParts = [`${encodeURIComponent(key)}=${encodeURIComponent(value)}`, `path=${path}`, `SameSite=${sameSite}`];

  const expiryDate = expires || (days ? new Date(Date.now() + days * 24 * 60 * 60 * 1000) : null);
  if (expiryDate) cookieParts.push(`expires=${expiryDate.toUTCString()}`);
  if (secure) cookieParts.push('Secure');

  document.cookie = cookieParts.join('; ');
}

async function resolveAccessToken(userData) {
  if (!userData?.token) return null;

  try {
    const { decryptData } = await import('./encryptDecryptData');
    return decryptData(userData.token) || userData.token;
  } catch (error) {
    console.error('Error decrypting user token:', error);
    return userData.token;
  }
}

export async function getHeaders() {
  const userDataValue = readCookie('userData');
  if (!userDataValue) return {};

  try {
    const userData = JSON.parse(userDataValue);
    const accessToken = await resolveAccessToken(userData);
    return accessToken ? { authorization: `Bearer ${accessToken}` } : {};
  } catch (error) {
    console.error('Error parsing user data:', error);
    return {};
  }
}

function buildRequestUrl(endPoint, data, method) {
  if ((method !== 'get' && method !== 'delete') || !data || typeof data !== 'object') {
    return endPoint;
  }

  const url = new URL(endPoint);
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    url.searchParams.set(key, String(value));
  });
  return url.toString();
}

async function parseResponse(response) {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  return response.text();
}

export async function apiReq(endPoint, data, method, headers = {}, requestOptions = {}) {
  try {
    const tokenHeaders = await getHeaders();
    const requestHeaders = {
      ...tokenHeaders,
      ...headers,
    };

    const config = {
      method: method.toUpperCase(),
      headers: requestHeaders,
      ...requestOptions,
    };

    if (method !== 'get' && method !== 'delete') {
      config.headers = {
        'Content-Type': 'application/json',
        ...requestHeaders,
      };
      config.body = JSON.stringify(data ?? {});
    }

    const response = await fetch(buildRequestUrl(endPoint, data, method), config);
    const responseData = await parseResponse(response);

    if (!response.ok || responseData?.status === false) {
      throw {
        status: response.status,
        ...(typeof responseData === 'object' && responseData !== null
          ? responseData
          : { message: response.statusText || 'Request failed' }),
      };
    }

    return { status: response.status, data: responseData };
  } catch (error) {
    if (error?.status) {
      throw error;
    }

    throw {
      status: 0,
      message: error?.message || 'Network Error',
    };
  }
}

export function setCookie(key, value, options = {}) {
  writeCookie(key, JSON.stringify(value), options);
}

export function getCookie(key) {
  return readCookie(key);
}

export function removeCookie(key, options = {}) {
  writeCookie(key, '', { ...options, expires: new Date(0) });
}

export function deleteAllCookies() {
  if (!isClient) return;

  document.cookie.split(';').forEach((cookie) => {
    const [rawKey] = cookie.split('=');
    const key = rawKey?.trim();
    if (key) {
      removeCookie(decodeURIComponent(key));
    }
  });
}

export function setUserAuthState(isAuthenticated) {
  if (isClient) {
    localStorage.setItem('isAuthenticated', String(isAuthenticated));
  }
}

export function getUserAuthState() {
  return isClient ? localStorage.getItem('isAuthenticated') === 'true' : false;
}

export function setLocalStorage(key, value) {
  if (isClient) {
    localStorage.setItem(key, value);
  }
}

export function getLocalStorage(key) {
  return isClient ? localStorage.getItem(key) : null;
}

export function deleteLocalStorage(key) {
  if (isClient) {
    localStorage.removeItem(key);
  }
}

export function clearAllLocalStorage() {
  if (isClient) {
    localStorage.clear();
  }
}

export function apiPost(endPoint, data, headers = {}) {
  return apiReq(endPoint, data, 'post', headers);
}

export function apiDelete(endPoint, data, headers = {}) {
  return apiReq(endPoint, data, 'delete', headers);
}

export function apiGet(endPoint, data, headers = {}, requestOptions) {
  return apiReq(endPoint, data, 'get', headers, requestOptions);
}

export function apiPut(endPoint, data, headers = {}) {
  return apiReq(endPoint, data, 'put', headers);
}

export function apiPatch(endPoint, data, headers = {}) {
  return apiReq(endPoint, data, 'patch', headers);
}
